import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from './api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Only check auth once on mount
    if (!authChecked) {
      checkAuth();
    }
  }, [authChecked]);

  const checkAuth = async () => {
    try {
      // Try developer endpoint first
      try {
        const { developers } = await import('./api');
        const devResponse = await developers.getMe();
        setUser({
          ...devResponse.data,
          user_type: 'developer'
        });
      } catch (devError) {
        // Not a developer, try admin endpoint
        const response = await auth.getMe();
        setUser({
          ...response.data,
          user_type: response.data.user_type || 'admin'
        });
      }
    } catch (error) {
      // Only log non-401 errors to avoid console spam
      if (error.response?.status !== 401) {
        console.error('Auth check failed:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  const login = async (password, recaptchaToken) => {
    try {
      await auth.login(password, recaptchaToken);
      await checkAuth();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
